import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X, Clock, Gift, Sparkles } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Recording Studio",
  "Production Room",
  "DJ Room",
  "Press Shots",
  "Photography (Dry Hire)",
  "Mixing / Mastering",
  "DJ Lessons",
  "Not sure yet",
];

export default function WelcomeOffer() {
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", service: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState("");
  const [timeLeft, setTimeLeft] = useState({ hours: 6, minutes: 0, seconds: 0 });

  // Only show on home page and booking page
  const allowedPaths = ["/", "/booking"];
  const isAllowedPage = allowedPaths.includes(location.pathname);

  useEffect(() => {
    if (!isAllowedPage) return;
    
    // Check if user has already seen/claimed the welcome offer
    const hasSeenOffer = localStorage.getItem("welcomeOffer_seen");
    const offerExpiry = localStorage.getItem("welcomeOffer_expiry");
    
    if (!hasSeenOffer) {
      // Show popup after a short delay (3 seconds)
      const timer = setTimeout(() => {
        setIsVisible(true);
        // Set 6 hour expiry
        const expiry = new Date().getTime() + (6 * 60 * 60 * 1000);
        localStorage.setItem("welcomeOffer_expiry", expiry.toString());
        localStorage.setItem("welcomeOffer_seen", "true");
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [isAllowedPage]);

  // Countdown timer
  useEffect(() => {
    if (!isVisible) return;

    const offerExpiry = localStorage.getItem("welcomeOffer_expiry");
    if (!offerExpiry) return;

    const interval = setInterval(() => {
      const expiryTime = parseInt(offerExpiry);
      const now = new Date().getTime();
      const diff = expiryTime - now;

      if (diff <= 0) {
        setTimeLeft({ hours: 0, minutes: 0, seconds: 0 });
        clearInterval(interval);
      } else {
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        setTimeLeft({ hours, minutes, seconds });
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isVisible]);

  const handleClose = () => {
    setIsVisible(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.name || !formData.email || !formData.phone || !formData.service) {
      setError("Please fill in all fields, including the service you're interested in");
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/welcome-offer/claim`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        service: formData.service,
        offer: "10% Off First Booking",
        discount: "10%"
      });

      localStorage.setItem("welcomeOffer_claimed", "true");
      setShowSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0, y: 50 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.8, opacity: 0, y: 50 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="relative max-w-lg w-full overflow-hidden rounded-3xl shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={handleClose}
            data-testid="welcome-offer-close"
            className="absolute top-4 right-4 z-20 text-white/80 hover:text-white bg-black/30 hover:bg-black/50 rounded-full p-2 transition-all"
          >
            <X className="w-5 h-5" />
          </button>

          {!showSuccess ? (
            <>
              {/* Image Section */}
              <div className="relative h-48 bg-gradient-to-br from-dream-violet via-purple-600 to-pink-500">
                <div className="absolute inset-0 bg-[url('https://customer-assets.emergentagent.com/job_dreammakers-studio/artifacts/recording-studio-bg.jpg')] bg-cover bg-center opacity-30" />
                <div className="absolute inset-0 bg-gradient-to-t from-dream-surface to-transparent" />
                
                {/* Floating elements */}
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="absolute top-6 left-6"
                >
                  <Sparkles className="w-8 h-8 text-yellow-400" />
                </motion.div>
                <motion.div
                  animate={{ y: [0, -8, 0] }}
                  transition={{ duration: 2.5, repeat: Infinity, delay: 0.5 }}
                  className="absolute top-10 right-16"
                >
                  <Gift className="w-6 h-6 text-pink-300" />
                </motion.div>

                {/* Offer Badge */}
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 z-10">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", delay: 0.3 }}
                    className="bg-gradient-to-r from-dream-violet to-pink-500 text-white px-8 py-4 rounded-2xl shadow-lg shadow-dream-violet/30"
                  >
                    <p className="text-3xl font-bold text-center">10% OFF</p>
                    <p className="text-sm text-white/90 text-center">Your First Booking</p>
                  </motion.div>
                </div>
              </div>

              {/* Content Section */}
              <div className="bg-dream-surface p-8 pt-14">
                <div className="text-center mb-6">
                  <h2 className="font-heading text-2xl font-bold text-white mb-2">
                    Welcome to Dream Makers!
                  </h2>
                  <p className="text-white/60 text-sm mb-4">
                    Claim your exclusive first-time visitor discount
                  </p>
                  
                  {/* Countdown Timer */}
                  <div className="inline-flex items-center gap-2 bg-red-500/20 text-red-400 px-4 py-2 rounded-full text-sm font-bold">
                    <Clock className="w-4 h-4" />
                    <span>Expires in: </span>
                    <span className="font-mono">
                      {String(timeLeft.hours).padStart(2, '0')}:
                      {String(timeLeft.minutes).padStart(2, '0')}:
                      {String(timeLeft.seconds).padStart(2, '0')}
                    </span>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <Input
                    type="text"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-dream-bg border-dream-border text-white placeholder:text-white/40"
                    required
                  />
                  <Input
                    type="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="bg-dream-bg border-dream-border text-white placeholder:text-white/40"
                    required
                  />
                  <Input
                    type="tel"
                    placeholder="Mobile Number"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="bg-dream-bg border-dream-border text-white placeholder:text-white/40"
                    required
                  />
                  <Select
                    value={formData.service}
                    onValueChange={(value) => setFormData({ ...formData, service: value })}
                  >
                    <SelectTrigger
                      className="bg-dream-bg border-dream-border text-white data-[placeholder]:text-white/40"
                      data-testid="welcome-offer-service"
                    >
                      <SelectValue placeholder="Which service are you interested in?" />
                    </SelectTrigger>
                    <SelectContent className="bg-dream-surface border-dream-border text-white">
                      {SERVICE_OPTIONS.map((opt) => (
                        <SelectItem key={opt} value={opt} className="focus:bg-dream-violet/30 focus:text-white">
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {error && (
                    <p className="text-red-400 text-sm text-center">{error}</p>
                  )}

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-dream-violet to-pink-500 hover:from-dream-violet-glow hover:to-pink-400 py-6 font-bold text-lg rounded-xl"
                  >
                    {isSubmitting ? "Claiming..." : "Claim 10% Off Now!"}
                  </Button>
                </form>

                <p className="text-white/40 text-xs text-center mt-4">
                  We&apos;ll reach out to help you book your discounted session
                </p>
              </div>
            </>
          ) : (
            /* Success State */
            <div className="bg-dream-surface p-8 text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring" }}
                className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6"
              >
                <Gift className="w-10 h-10 text-green-500" />
              </motion.div>

              <h3 className="font-heading text-2xl font-bold text-white mb-3">
                You&apos;re All Set!
              </h3>
              <p className="text-white/60 mb-6">
                Check your email for confirmation. We&apos;ll be in touch shortly to help you redeem your 10% discount!
              </p>

              <div className="inline-flex items-center gap-2 bg-dream-violet/20 text-dream-violet px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Clock className="w-4 h-4" />
                Valid for 6 hours
              </div>

              <Button
                onClick={handleClose}
                className="w-full bg-dream-violet hover:bg-dream-violet-glow py-6 rounded-xl"
              >
                Start Browsing
              </Button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
